/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console FileDrop firebase navigator */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Service from '../Util/Service';
import Combobox from '../Combobox/Combobox';
import Log from './Log';
import $ from '../Util/Util';
import Firebase from '../Util/Firebase';
import Datetime from '../Util/Datetime';
import Label from '../Label/Label';
import Button from '../Button/Button';
import Textbox from '../Textbox/Textbox';
import Numericbox from '../Numericbox/Numericbox';
import Datebox from '../Datebox/Datebox';
import Textarea from '../Textarea/Textarea';
import Radio from '../Radio/Radio';
import Checkbox from '../Checkbox/Checkbox';
import AddIcon from './baseline_add_black_24dp.png';
import DeleteIcon from './baseline_delete_black_24dp.png';
import CopyIcon from './baseline_file_copy_black_24dp.png';
import SaveIcon from './baseline_check_black_24dp.png';
import CancelIcon from './baseline_close_black_24dp.png';
import './Entityview.css';

/**
 * Represents the entity view.
 */

export default class Entityview extends Component {

    /**
     * Properties.
     */

    static propTypes = {
        context:                PropTypes.object.isRequired,
        onLoading:              PropTypes.func.isRequired,
        onClose:                PropTypes.func.isRequired,
        onDelete:               PropTypes.func,
        onNewRelation:          PropTypes.func,
        onGotoRelation:         PropTypes.func,
        onPrimaryValueChanged:  PropTypes.func,
        entitytypes:            PropTypes.array,
        users:                  PropTypes.array,
        id:                     PropTypes.string,
        readonly:               PropTypes.bool
    }

    /**
     * Default state.
     */

    state = {
        entity:                 null,
        version:                [],
        comment:                [],
        edit:                   this.props.id ? false : true,
        selectedversion:        0,
        entitytype:             null,
        readonly:               false,
        creationentitytypes:    [],
        users:                  null,
        showlog:                false,
        addcomment:             false
    }

    /**
     * Loads the entity specified by ID.
     * 
     * @param   {Object}    entitytoload    entity that has to load
     * @param   {Boolean}   readonly        read only access
     */

    loadEntity = (entitytoload, readonly) => {

        // Creating and returning the promise object

        return new Promise((resolve, reject) => {

            // Showing the progress indicator

            this.props.onLoading(true);

            // Loading the entity

            Service.loadEntity(entitytoload.id).then((entity) => {

                // Storing the entity path

                entity.path = entitytoload.path || entity.path;

                // Getting the entity type of the entity

                for (let i = 0; i < this.props.entitytypes.length; i++) {
                    if (this.props.entitytypes[i].id === entity.entitytype) {
                        entity.entitytype = this.props.entitytypes[i];
                        break;
                    }
                }

                // Getting the attributes of the versions

                let version = [];

                Object.keys(entity.versions).forEach((versionKey) => {
                    version.push(versionKey.substring(1));

                    for (let j = 0; j < this.props.users.length; j++) {
                        if (this.props.users[j].uid === entity.versions[versionKey].modifiedby) {
                            entity.versions[versionKey].modifiedby = this.props.users[j];
                            break;
                        }
                    }

                    let attributes = [];

                    entity.entitytype.attributes.forEach((attrib) => {
                        let attr = JSON.parse(JSON.stringify(attrib));
                        attr.value = (entity.versions[versionKey].attributes[attrib.id] === null || 
                                      entity.versions[versionKey].attributes[attrib.id] === undefined) ? (attr.multivalue ? [''] : '') :
                                      entity.versions[versionKey].attributes[attrib.id];

                        if (['picture', 'file'].indexOf(attr.type) >= 0 || attr.multivalue) {
                            attr.origvalue = attr.value;
                        }

                        if (attr.type === 'user') {
                            for (let j = 0; j < this.props.users.length; j++) {
                                if (this.props.users[j].uid === entity.versions[versionKey].attributes[attrib.id]) {
                                    attr.user = this.props.users[j];
                                    break;
                                }
                            }
                        }

                        attributes.push(attr);
                    });

                    // Sorting the attribute by primary flag and name

                    attributes.sort((a, b) => {
                        let nameA = a.name.toUpperCase();
                        let nameB = b.name.toUpperCase();

                        if (a.primary) {
                            return -1;
                        } else if (b.primary) {
                            return 1;
                        } else {
                            if (nameA < nameB) {
                                return -1;
                            } else if (nameA > nameB) {
                                return 1;
                            } else {
                                return 0;
                            }
                        }
                    });

                    entity.versions[versionKey].attributes = attributes;
                });

                // Sorting the versions

                version.sort((a, b) => {
                    return parseInt(a, 10) - parseInt(b, 10);
                });

                let v = version[version.length - 1];

                // Getting the comments

                let comment = [];

                if (entity.comments) {
                    Object.keys(entity.comments).forEach((commentKey) => {
                        entity.comments[commentKey].id = commentKey;
                        entity.comments[commentKey].date = new Date(entity.comments[commentKey].date);
                        comment.push(entity.comments[commentKey]);
                    });

                    // Sorting the comments

                    comment.sort((a, b) => {
                        return a.date - b.date;
                    });
                }

                // Setting the entity access

                entity.writable = false;

                if (!readonly) {
                    if (this.props.context.user.admin) {
                        entity.writable = true;
                    } else {
                        for (let j = 0; j < entity.entitytype.entitycreateaccess.length; j++) {
                            if (entity.entitytype.entitycreateaccess[j].id === this.props.context.user.group.id) {
                                entity.writable = true;
                                break;
                            }
                        }
                    }
                }

                // Storing the entity

                this.setState({ entity: entity, version: version, comment: comment, selectedversion: 'v' + v, readonly: readonly });

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Resolving

                resolve();
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });

                // Rejecting

                reject();
            });
        });
    }

    newEntity = (path) => {
        let creationentitytypes = [];

        this.props.entitytypes.forEach((item) => {
            if (this.props.context.user.admin) {
                creationentitytypes.push(item);
            } else {
                for (let i = 0; i < item.entitycreateaccess.length; i++) {
                    if (item.entitycreateaccess[i].id === this.props.context.user.group.id) {
                        creationentitytypes.push(item);
                        break;
                    }
                }
            }
        });
        
        if (creationentitytypes.length > 0) {
            let entitytype = JSON.parse(JSON.stringify(creationentitytypes[0]));

            this.setState({ 
                entity:             { path: path }, 
                edit:               true, 
                entitytype:         entitytype,
                selectedversion:    'v1',
                creationentitytypes: creationentitytypes
            }, () => {
                this.entitytype.setValue(entitytype.id);
                this._onEntityTypeSelected(entitytype);
            });
        } else {
            $.fireEvent('snackbar', { message: this.props.context.locale.dashboard.noentitycreationaccess });
        }
    }

    _modify = () => {
        let entity = this.state.entity;

        if (entity.path.indexOf('entities') < 0) {
            entity.path = entity.path + '/entities/' + entity.id;
        }

        this.setState({ addcomment: false, entity: entity, edit: true }, () => {
            this.state.entity.versions[this.state.selectedversion].attributes.forEach((item, index) => {
                if (['text', 'multilinetext', 'externallink', 'integer', 'float'].indexOf(item.type) >= 0) {
                    if (item.multivalue) {
                        item.value.forEach((i, ind) => {
                            this['attribute' + item.id + '_' + ind].setValue(i);
                        });
                    } else {
                        this['attribute' + item.id].setValue(item.value);
                    }
                } else if (item.type === 'date' && item.value) {
                    if (item.multivalue) {
                        item.value.forEach((i, ind) => {
                            this['attribute' + item.id + '_' + ind].setValue(new Date(i));
                        });
                    } else {
                        this['attribute' + item.id].setValue(new Date(item.value));
                    }
                } else if (item.type === 'picture') {
                    let picture = document.getElementById(`entityview-picture-${item.id}`);
                    let dropZone = new FileDrop(`entityview-picture-${item.id}`);
                    let uploaderFileInput = picture.getElementsByTagName('input');

                    for(let item in uploaderFileInput) {
                        if(uploaderFileInput[item].getAttribute('type') === 'file') {                           
                            uploaderFileInput[item].addEventListener('click', function() {
                                this.value = null;
                            });

                            break;
                        }
                    }

                    dropZone.event('send', (files) => {
                        if (files && files.length > 0) {
                            files[0].readData((content) => {
                                let entity = this.state.entity;
                                let extension;
                                let pos = files[0].name.lastIndexOf('.');

                                if (pos >= 0) {
                                    extension = files[0].name.substring(pos + 1);
                                } else {
                                    extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                                }

                                let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10) + 1}-${item.id}.${extension}`;
                                entity.versions[this.state.selectedversion].attributes[index].value = { id: item.id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` };
                                this['attribute' + item.id].content = entity.versions[this.state.selectedversion].attributes[index].value;
                                this.setState({ entity: entity });
                            });
                        }
                    });

                    this['attribute' + item.id].origvalue = item.origvalue;
                } else if (item.type === 'file') {
                    if (item.multivalue) {
                        if (item.value) {
                            item.value.forEach((i, ind) => {
                                this['attribute' + item.id + '_' + ind].origvalue = item.origvalue[ind];

                                let file = document.getElementById(`entityview-file-${item.id}_${ind}`);
                                let dropZone = new FileDrop(`entityview-file-${item.id}_${ind}`);
                                let uploaderFileInput = file.getElementsByTagName('input');
        
                                for(let item in uploaderFileInput) {
                                    if(uploaderFileInput[item].getAttribute('type') === 'file') {
                                        uploaderFileInput[item].style.width = '100px';
                                        uploaderFileInput[item].style.height = '100px';
                                        uploaderFileInput[item].style.top = '-50px';
                                        uploaderFileInput[item].style.left = '-50px';
                                        uploaderFileInput[item].addEventListener('click', function() {
                                            this.value = null;
                                        });
        
                                        break;
                                    }
                                }
        
                                dropZone.event('send', (files) => {
                                    if (files && files.length > 0) {
                                        files[0].readData((content) => {
                                            let entity = this.state.entity;
                                            let extension;
                                            let pos = files[0].name.lastIndexOf('.');
        
                                            if (pos >= 0) {
                                                extension = files[0].name.substring(pos + 1);
                                            } else {
                                                extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                                            }
        
                                            let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10) + 1}-${item.id}.${extension}`;
                                            entity.versions[this.state.selectedversion].attributes[index].value[ind] = { id: item.id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` };
                                            this['attribute' + item.id + '_' + ind].content = entity.versions[this.state.selectedversion].attributes[index].value[ind];
                                            this.setState({ entity: entity });
                                        });
                                    }
                                });
                            });
                        }
                    } else {
                        let file = document.getElementById(`entityview-file-${item.id}`);
                        let dropZone = new FileDrop(`entityview-file-${item.id}`);
                        let uploaderFileInput = file.getElementsByTagName('input');

                        for(let item in uploaderFileInput) {
                            if(uploaderFileInput[item].getAttribute('type') === 'file') {
                                uploaderFileInput[item].style.width = '100px';
                                uploaderFileInput[item].style.height = '100px';
                                uploaderFileInput[item].style.top = '-50px';
                                uploaderFileInput[item].style.left = '-50px';
                                uploaderFileInput[item].addEventListener('click', function() {
                                    this.value = null;
                                });

                                break;
                            }
                        }

                        dropZone.event('send', (files) => {
                            if (files && files.length > 0) {
                                files[0].readData((content) => {
                                    let entity = this.state.entity;
                                    let extension;
                                    let pos = files[0].name.lastIndexOf('.');

                                    if (pos >= 0) {
                                        extension = files[0].name.substring(pos + 1);
                                    } else {
                                        extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                                    }

                                    let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10) + 1}-${item.id}.${extension}`;
                                    entity.versions[this.state.selectedversion].attributes[index].value = { id: item.id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` };
                                    this['attribute' + item.id].content = entity.versions[this.state.selectedversion].attributes[index].value;
                                    this.setState({ entity: entity });
                                });
                            }
                        });

                        this['attribute' + item.id].origvalue = item.origvalue;
                    }
                } else if (item.type === 'radio') {
                    for (let i = 0; i < item.values.length; i++) {
                        if (item.values[i] === item.value) {
                            this['attribute' + item.id + i].setValue(true);
                            break;
                        }
                    }
                } else if (item.type === 'checkbox') {
                    for (let i = 0; i < item.values.length; i++) {
                        if (item.value.indexOf(item.values[i]) >= 0) {
                            this['attribute' + item.id + i].setValue(true);
                        }
                    }
                } else if (item.type === 'user') {
                    this['attribute' + item.id].setValue(item.value);
                }
            });

            let tags = '';

            if (this.state.entity.tags) {
                this.state.entity.tags.forEach((item, index) => {
                    tags += (index > 0 ? ',' : '') + item;
                });
            }

            this.tags.setValue(tags);
            ReactDOM.findDOMNode(this.scrollcontainer).scrollTop = 0;
        });
    }

    _base64ArrayBuffer = (arrayBuffer) => {
        let base64    = ''
        let encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
      
        let bytes         = new Uint8Array(arrayBuffer)
        let byteLength    = bytes.byteLength
        let byteRemainder = byteLength % 3
        let mainLength    = byteLength - byteRemainder
      
        let a, b, c, d
        let chunk
      
        // Main loop deals with bytes in chunks of 3
        for (let i = 0; i < mainLength; i = i + 3) {
          // Combine the three bytes into a single integer
          chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]
      
          // Use bitmasks to extract 6-bit segments from the triplet
          a = (chunk & 16515072) >> 18 // 16515072 = (2^6 - 1) << 18
          b = (chunk & 258048)   >> 12 // 258048   = (2^6 - 1) << 12
          c = (chunk & 4032)     >>  6 // 4032     = (2^6 - 1) << 6
          d = chunk & 63               // 63       = 2^6 - 1
      
          // Convert the raw binary segments to the appropriate ASCII encoding
          base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d]
        }
      
        // Deal with the remaining bytes and padding
        if (byteRemainder === 1) {
          chunk = bytes[mainLength]
      
          a = (chunk & 252) >> 2 // 252 = (2^6 - 1) << 2
      
          // Set the 4 least significant bits to zero
          b = (chunk & 3)   << 4 // 3   = 2^2 - 1
      
          base64 += encodings[a] + encodings[b] + '=='
        } else if (byteRemainder === 2) {
          chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1]
      
          a = (chunk & 64512) >> 10 // 64512 = (2^6 - 1) << 10
          b = (chunk & 1008)  >>  4 // 1008  = (2^6 - 1) << 4
      
          // Set the 2 least significant bits to zero
          c = (chunk & 15)    <<  2 // 15    = 2^4 - 1
      
          base64 += encodings[a] + encodings[b] + encodings[c] + '='
        }
        
        return base64
    }

    _cancel = () => {
        if (this.props.id === 'newentity') {
            this.props.onClose(this.props.id);
        } else {
            this.setState({ edit: false }, () => {
                ReactDOM.findDOMNode(this.scrollcontainer).scrollTop = 0;
            });
        }
    }

    /**
     * Trying to save the entity.
     */

    _save = () => {

        // Validating

        let errortype = 0;
        let attributes;

        if (this.state.entity.id) {
            attributes = this.state.entity.entitytype.attributes;
        } else {
            attributes = this.state.entitytype.attributes;
        }

        attributes.forEach((item) => {
            if (errortype === 0) {
                if (item.mandatory) {
                    if (['text', 'multilinetext', 'externallink', 'integer', 'float', 'date'].indexOf(item.type) >= 0) {
                        if (item.multivalue) {
                            if (this['attribute' + item.id + '_0'].isEmpty()) {
                                errortype = 1;
                            }
                        } else {
                            if (this['attribute' + item.id].isEmpty()) {
                                errortype = 1;
                            }
                        }
                    } else if (item.type === 'picture') {
                        if (this['attribute' + item.id].src === '') {
                            errortype = 1;
                        }
                    } else if (item.type === 'file') {
                        if (!item.value) {
                            errortype = 1;
                        }
                    } else if (item.type === 'checkbox') {
                        let checked = false;

                        for (let i = 0; i < item.values.length; i++) {
                            if (this['attribute' + item.id + i].getValue()) {
                                checked = true;
                                break;
                            }
                        }

                        if (!checked) {
                            errortype = 1;
                        }
                    } else if (item.type === 'user') {
                        if (this['attribute' + item.id].getItem().uid === '-') {
                            errortype = 1;
                        }
                    }
                }
                
                if (errortype === 0) {
                    if (['integer', 'float'].indexOf(item.type) >= 0) {
                        if (item.multivalue) {
                            let i = 0;

                            while (true) {
                                if (this['attribute' + item.id + '_' + i] && !this['attribute' + item.id + '_' + i].isEmpty()) {
                                    let value = this['attribute' + item.id + '_' + i].getValue();
                                    let numvalue = item.type === 'integer' ? parseInt(value, 10) : parseFloat(value);

                                    if (value !== '' && (numvalue < item.minvalue || numvalue > item.maxvalue)) {
                                        errortype = 2;
                                        break;
                                    }
                                } else {
                                    break;
                                }
        
                                i++;
                            }
                        } else {
                            let value = this['attribute' + item.id].getValue();
                            let numvalue = item.type === 'integer' ? parseInt(value, 10) : parseFloat(value);

                            if (value !== '' && (numvalue < item.minvalue || numvalue > item.maxvalue)) {
                                errortype = 2;
                            }
                        }
                    }
                }
            }
        });

        if (errortype !== 0) {

            // Showing error

            $.fireEvent('snackbar', { 
                message: errortype === 1 ? this.props.context.locale.dashboard.validation.required :
                                           this.props.context.locale.dashboard.validation.outofrange,
                error: true 
            });
            return;
        }

        // Uploading pictures and files

        let uploader = (item) => {
            return new Promise((resolve, reject) => {
                Firebase.uploadFile(item.url, item.value, item.mime).then((url) => {
                    resolve(url);
                }).catch((error) => {

                    // Showing error

                    $.fireEvent('snackbar', { message: this.props.context.locale.dashboard.file.uploaderror, error: true });
                    reject();
                });
            });
        };

        // Showing the progress indicator

        this.props.onLoading(true);

        let entity = {};

        if (this.state.entity.access) {
            entity.access = this.state.entity.access;
        }

        if (this.props.id === 'newentity') {
            entity.entitytype = this.state.entitytype.id;
        } else {
            entity.entitytype = this.state.entity.entitytype.id;
        }

        if (!this.tags.isEmpty()) {
            let tags = this.tags.getValue().split(',');

            for (let i = 0; i < tags.length; i++) {
                tags[i] = tags[i].trim();
            }

            entity.tags = tags;
        }

        if (this.state.entity.relations) {
            entity.relations = this.state.entity.relations;
        }

        let version;
        let versions;

        if (this.state.entity.id) {
            entity.id = this.state.entity.id;
            version = 'v' + (this.state.version.length + 1);
            versions = JSON.parse(JSON.stringify(this.state.entity.versions));

            Object.keys(versions).forEach((key) => {
                let attr = {};

                versions[key].attributes.forEach((item) => {
                    if (['picture', 'file'].indexOf(item.type) >= 0 && !item.multivalue && (item.origvalue || item.origvalue === '')) {
                        attr[item.id] = item.origvalue;

                        if (this['attribute' + item.id + '_0']) {
                            this['attribute' + item.id + '_0'].origvalue = item.origvalue;
                        }
                    } else if (['picture', 'file'].indexOf(item.type) >= 0 && item.multivalue) {
                        attr[item.id] = item.origvalue;
                    } else if (item.multivalue) {
                        attr[item.id] = item.origvalue;
                    } else {
                        attr[item.id] = item.value;
                    }
                });

                versions[key].attributes = attr;
                versions[key].modifiedby = versions[key].modifiedby.uid;
                versions[key].modificationdate = this.state.entity.versions[key].modificationdate;
            });
        } else {
            versions = {};
            version = 'v1';
        }

        versions[version] = {
            modificationdate:   firebase.database.ServerValue.TIMESTAMP,
            modifiedby:         this.props.context.user.uid,
            attributes:         {}
        };

        let uploadQueue = [];
        let attr;

        if (this.state.entity.id) {
            attr = this.state.entity.entitytype.attributes;
        } else {
            attr = this.state.entitytype.attributes;
        }

        attr.forEach((item) => {
            if (item.primary) {
                entity.primaryvalue = this['attribute' + item.id].getValue();
            }

            if (['text', 'multilinetext', 'externallink', 'integer', 'float'].indexOf(item.type) >= 0) {
                if (item.multivalue) {
                    let i = 0;
                    let value = [];

                    while (true) {
                        if (this['attribute' + item.id + '_' + i] && !this['attribute' + item.id + '_' + i].isEmpty()) {
                            value.push(this['attribute' + item.id + '_' + i].getValue());
                        } else {
                            break;
                        }

                        i++;
                    }

                    versions[version].attributes[item.id] = value;
                } else {
                    versions[version].attributes[item.id] = this['attribute' + item.id].getValue();
                }
            } else if (item.type === 'date') {
                if (item.multivalue) {
                    let i = 0;
                    let value = [];

                    while (true) {
                        if (this['attribute' + item.id + '_' + i] && !this['attribute' + item.id + '_' + i].isEmpty()) {
                            value.push(this['attribute' + item.id + '_' + i].getValue().getTime());
                        } else {
                            break;
                        }

                        i++;
                    }

                    versions[version].attributes[item.id] = value;
                } else {
                    versions[version].attributes[item.id] = this['attribute' + item.id].isEmpty() ? null : this['attribute' + item.id].getValue().getTime();
                }
            } else if (item.type === 'picture') {
                if (this['attribute' + item.id].content) {
                    uploadQueue.push({ id: item.id, type: 'picture', content: this['attribute' + item.id].content });
                } else {
                    versions[version].attributes[item.id] = this['attribute' + item.id].origvalue || '';
                }
            } else if (item.type === 'file') {
                if (item.multivalue) {
                    if (!versions[version].attributes[item.id]) {
                        versions[version].attributes[item.id] = [];
                    }

                    let i = 0;

                    while (true) {
                        if (!this['attribute' + item.id + '_' + i]) {
                            break;
                        }

                        if (versions[version].attributes[item.id].length < (i + 1)) {
                            for (let j = versions[version].attributes[item.id].length; j < (i + 1); j++) {
                                versions[version].attributes[item.id].push({});
                            }
                        }

                        if (this['attribute' + item.id + '_' + i].content) {
                            uploadQueue.push({ id: item.id, index: i, type: 'file', content: this['attribute' + item.id + '_' + i].content });
                        } else {
                            versions[version].attributes[item.id][i] = this['attribute' + item.id + '_' + i].origvalue || null;
                        }

                        i++;
                    }
                } else {
                    if (this['attribute' + item.id + '_0'].content) {
                        uploadQueue.push({ id: item.id, type: 'file', content: this['attribute' + item.id + '_0'].content });
                    } else {
                        versions[version].attributes[item.id] = this['attribute' + item.id + '_0'].origvalue || null;
                    }
                }
            } else if (item.type === 'radio') {
                for (let i = 0; i < item.values.length; i++) {
                    if (this['attribute' + item.id + i].getValue()) {
                        versions[version].attributes[item.id] = item.values[i];
                        break;
                    }
                }
            } else if (item.type === 'checkbox') {
                versions[version].attributes[item.id] = [];

                for (let i = 0; i < item.values.length; i++) {
                    if (this['attribute' + item.id + i].getValue()) {
                        versions[version].attributes[item.id].push(item.values[i]);
                    }
                }
            } else if (item.type === 'user') {
                versions[version].attributes[item.id] = this['attribute' + item.id].getItem().uid;
            }
        });

        let clearQueue = (index, callback) => {
            if (index >= uploadQueue.length) {
                callback();
            } else {
                uploader(uploadQueue[index].content).then((url) => {
                    if (uploadQueue[index].index !== undefined) {
                        versions[version].attributes[uploadQueue[index].id][uploadQueue[index].index] = uploadQueue[index].type === 'picture' ? url : { name: uploadQueue[index].content.name, url: url };
                    } else {
                        versions[version].attributes[uploadQueue[index].id] = uploadQueue[index].type === 'picture' ? url : { name: uploadQueue[index].content.name, url: url };
                    }

                    clearQueue(index + 1, callback);
                });
            }
        };

        let comments = {};

        this.state.comment.forEach((c) => {
            comments[c.id] = { comment: c.comment, date: c.date.getTime(), user: c.user };
        });

        clearQueue(0, () => {
            entity.versions = versions;
            entity.path = this.state.entity.path;
            entity.comments = comments;
            let path = entity.path;
            let primaryvalue = entity.primaryvalue;

            Service.saveEntity(entity).then((id) => {
                if (this.props.id === 'newentity') {
                    this.setState({ edit: false }, () => {
                        if (!this.props.context.environment.browser.info.mobile) {
                            this.props.onPrimaryValueChanged(path, primaryvalue, id, entity.entitytype);
                        }

                        this.loadEntity({ id: id, path: path }, this.state.readonly);
                    });
                } else {
                    this.setState({ edit: false }, () => {
                        if (!this.props.context.environment.browser.info.mobile) {
                            this.props.onPrimaryValueChanged(this.state.entity.path, primaryvalue);
                        }

                        this.loadEntity(this.state.entity, this.state.readonly);
                    });
                }
            });
        });
    }

    /**
     * Opens the specified external link in a new window.
     * 
     * @param   {String}    link    external link that has to open
     */

    _openExternalLink = (link) => {
        window.open(link);
    }

    _deleteRelation = (index) => {
        let entity = this.state.entity;

        entity.relations.splice(index, 1);

        this.setState({ entity: entity });
    }

    /**
     * Event that fires when the user selects an entity type.
     * 
     * @param   {Object}    entitytype    selected entity type
     */

    _onEntityTypeSelected = (entitytype) => {
        
        // Sorting the attribute by primary flag and name

        entitytype.attributes.sort((a, b) => {
            let nameA = a.name.toUpperCase();
            let nameB = b.name.toUpperCase();

            if (a.primary) {
                return -1;
            } else if (b.primary) {
                return 1;
            } else {
                if (nameA < nameB) {
                    return -1;
                } else if (nameA > nameB) {
                    return 1;
                } else {
                    return 0;
                }
            }
        });

        entitytype.attributes.forEach((attr) => {
            if (attr.multivalue) {
                attr.value = [{}];
            }
        });

        this.setState({ entitytype: entitytype }, () => {
            entitytype.attributes.forEach((item, index) => {
                if (item.type === 'picture') {
                    let picture = document.getElementById(`entityview-picture-${item.id}`);
                    let dropZone = new FileDrop(`entityview-picture-${item.id}`);
                    let uploaderFileInput = picture.getElementsByTagName('input');

                    for(let item in uploaderFileInput) {
                        if(uploaderFileInput[item].getAttribute('type') === 'file') {                           
                            uploaderFileInput[item].addEventListener('click', function() {
                                this.value = null;
                            });

                            break;
                        }
                    }

                    dropZone.event('send', (files) => {
                        if (files && files.length > 0) {
                            files[0].readData((content) => {
                                let extension;
                                let pos = files[0].name.lastIndexOf('.');

                                if (pos >= 0) {
                                    extension = files[0].name.substring(pos + 1);
                                } else {
                                    extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                                }

                                let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10)}-${item.id}.${extension}`;
                                let entitytype = JSON.parse(JSON.stringify(this.state.entitytype));
                                entitytype.attributes[index].value = `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}`;
                                entitytype.attributes[index].mime = files[0].mime;
                                this['attribute' + item.id].content = { id: item.id, url: url, name: files[0].name, mime: files[0].mime, value: entitytype.attributes[index].value };
                                this.setState({ entitytype: entitytype });
                            });
                        }
                    });
                } else if (item.type === 'file') {
                    let file = document.getElementById(`entityview-file-${item.id}${item.multivalue ? '_0' : ''}`);
                    let dropZone = new FileDrop(`entityview-file-${item.id}${item.multivalue ? '_0' : ''}`);
                    let uploaderFileInput = file.getElementsByTagName('input');

                    for(let item in uploaderFileInput) {
                        if(uploaderFileInput[item].getAttribute('type') === 'file') {
                            uploaderFileInput[item].style.width = '100px';
                            uploaderFileInput[item].style.height = '100px';
                            uploaderFileInput[item].style.top = '-50px';
                            uploaderFileInput[item].style.left = '-50px';
                            uploaderFileInput[item].addEventListener('click', function() {
                                this.value = null;
                            });

                            break;
                        }
                    }

                    dropZone.event('send', (files) => {
                        if (files && files.length > 0) {
                            files[0].readData((content) => {
                                let extension;
                                let pos = files[0].name.lastIndexOf('.');

                                if (pos >= 0) {
                                    extension = files[0].name.substring(pos + 1);
                                } else {
                                    extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                                }

                                let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10)}-${item.id}.${extension}`;
                                let entitytype = JSON.parse(JSON.stringify(this.state.entitytype));
                                entitytype.attributes[index].value = [{ id: item.id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` }];
                                this['attribute' + item.id + '_0'].content = entitytype.attributes[index].value[0];
                                this.setState({ entitytype: entitytype });
                            });
                        }
                    });
                } else if (item.type === 'radio') {
                    for (let i = 0; i < item.values.length; i++) {
                        if (item.values[i] === item.defaultvalue) {
                            this['attribute' + item.id + i].setValue(true);
                            break;
                        }
                    }
                } else if (['text', 'multilinetext', 'integer', 'float', 'radio'].indexOf(item.type) >= 0) {
                    this['attribute' + item.id + (item.multivalue ? '_0' : '')].setValue(item.defaultvalue, true);
                } else if (item.type === 'date' && item.defaultvalue === 'today') {
                    this['attribute' + item.id + (item.multivalue ? '_0' : '')].setValue(new Date());
                } else if (item.type === 'user' && this.props.id === 'newentity') {
                    this['attribute' + item.id].setValue('-');
                }
            });
        });
    }

    _deleteFile = (index) => {
        if (this.props.id === 'newentity') {
            let entitytype = this.state.entitytype;
            entitytype.attributes[index].value = null;
            this.setState({ entitytype: entitytype });
        } else {
            let entity = this.state.entity;
            entity.versions[this.state.selectedversion].attributes[index].value = null;
            this.setState({ entity: entity });
        }
    }

    _copyURL = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            $.fireEvent('snackbar', { message: this.props.context.locale.dashboard.copiedtoclipboard });
        });
    }

    _toggleCheckbox = (checkbox) => {
        this[checkbox].toggle();
    }

    _deleteMultivalueOnNew = (index, ind) => {
        let entitytype = this.state.entitytype;
        let values = [];

        if (entitytype.attributes[index].type === 'file') {
            if (entitytype.attributes[index].value.length === 1) {
                entitytype.attributes[index].value = [{}];
                this.setState({ entitytype: entitytype }, () => {
                    delete this['attribute' + entitytype.attributes[index].id + '_0'].content;
                });
            } else {
                entitytype.attributes[index].value.forEach((item, i) => {
                    if (i !== ind) {
                        values.push(this['attribute' + entitytype.attributes[index].id + '_' +  i].content);
                    }
                });

                entitytype.attributes[index].value.splice(ind, 1);
                this.setState({ entitytype: entitytype }, () => {
                    values.forEach((item, i) => {
                        this['attribute' + entitytype.attributes[index].id + '_' +  i].content = item;
                    });
                });
            }
        } else {
            entitytype.attributes[index].value.forEach((item, i) => {
                if (i !== ind) {
                    values.push(this['attribute' + entitytype.attributes[index].id + '_' +  i].getValue());
                }
            });

            entitytype.attributes[index].value.splice(ind, 1);
            this.setState({ entitytype: entitytype }, () => {
                values.forEach((item, i) => {
                    this['attribute' + entitytype.attributes[index].id + '_' +  i].setValue(item);
                });
            });
        }
    }

    _deleteMultivalueOnModify = (index, ind) => {
        let entity = JSON.parse(JSON.stringify(this.state.entity));
        let values = [];
        let origvalues = [];

        if (entity.versions[this.state.selectedversion].attributes[index].type === 'file') {
            if (entity.versions[this.state.selectedversion].attributes[index].value.length === 1) {
                entity.versions[this.state.selectedversion].attributes[index].value = [{}];
                this.setState({ entity: entity }, () => {
                    delete this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_0'].content;
                    delete this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_0'].origvalue;
                });
            } else {
                entity.versions[this.state.selectedversion].attributes[index].value.forEach((item, i) => {
                    if (i !== ind) {
                        values.push(this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].content);
                        origvalues.push(this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].origvalue);
                    }
                });

                entity.versions[this.state.selectedversion].attributes[index].value.splice(ind, 1);
                this.setState({ entity: entity }, () => {
                    values.forEach((item, i) => {
                        this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].content = item;
                        this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].origvalue = origvalues[i];
                    });
                });
            }
        } else {
            entity.versions[this.state.selectedversion].attributes[index].value.forEach((item, i) => {
                if (i !== ind) {
                    values.push(this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].getValue());
                }
            });

            entity.versions[this.state.selectedversion].attributes[index].value.splice(ind, 1);
            this.setState({ entity: entity }, () => {
                values.forEach((item, i) => {
                    this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' +  i].setValue(item);
                });
            });
        }
    }

    _addMultivalueOnNew = (index) => {
        let entitytype = this.state.entitytype;
        entitytype.attributes[index].value.push({});
        this.setState({ entitytype: entitytype }, () => {
            if (entitytype.attributes[index].type === 'file') {
                let file = document.getElementById(`entityview-file-${entitytype.attributes[index].id}_${entitytype.attributes[index].value.length - 1}`);
                let dropZone = new FileDrop(`entityview-file-${entitytype.attributes[index].id}_${entitytype.attributes[index].value.length - 1}`);
                let uploaderFileInput = file.getElementsByTagName('input');

                for(let item in uploaderFileInput) {
                    if(uploaderFileInput[item].getAttribute('type') === 'file') {
                        uploaderFileInput[item].style.width = '100px';
                        uploaderFileInput[item].style.height = '100px';
                        uploaderFileInput[item].style.top = '-50px';
                        uploaderFileInput[item].style.left = '-50px';
                        uploaderFileInput[item].addEventListener('click', function() {
                            this.value = null;
                        });

                        break;
                    }
                }

                dropZone.event('send', (files) => {
                    if (files && files.length > 0) {
                        files[0].readData((content) => {
                            let extension;
                            let pos = files[0].name.lastIndexOf('.');

                            if (pos >= 0) {
                                extension = files[0].name.substring(pos + 1);
                            } else {
                                extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                            }

                            let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10)}-${this.state.entitytype.attributes[index].id}.${extension}`;
                            let entitytype = JSON.parse(JSON.stringify(this.state.entitytype));
                            entitytype.attributes[index].value[entitytype.attributes[index].value.length - 1] = { id: entitytype.attributes[index].id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` };
                            this['attribute' + entitytype.attributes[index].id + '_' + (entitytype.attributes[index].value.length - 1)].content = entitytype.attributes[index].value[entitytype.attributes[index].value.length - 1];
                            this.setState({ entitytype: entitytype });
                        });
                    }
                });
            }
        });
    }

    _addMultivalueOnModify = (index) => {
        let entity = JSON.parse(JSON.stringify(this.state.entity));
        entity.versions[this.state.selectedversion].attributes[index].value.push({});
        this.setState({ entity: entity }, () => {
            if (entity.versions[this.state.selectedversion].attributes[index].type === 'file') {
                let file = document.getElementById(`entityview-file-${entity.versions[this.state.selectedversion].attributes[index].id}_${entity.versions[this.state.selectedversion].attributes[index].value.length - 1}`);
                let dropZone = new FileDrop(`entityview-file-${entity.versions[this.state.selectedversion].attributes[index].id}_${entity.versions[this.state.selectedversion].attributes[index].value.length - 1}`);
                let uploaderFileInput = file.getElementsByTagName('input');

                for(let item in uploaderFileInput) {
                    if(uploaderFileInput[item].getAttribute('type') === 'file') {
                        uploaderFileInput[item].style.width = '100px';
                        uploaderFileInput[item].style.height = '100px';
                        uploaderFileInput[item].style.top = '-50px';
                        uploaderFileInput[item].style.left = '-50px';
                        uploaderFileInput[item].addEventListener('click', function() {
                            this.value = null;
                        });

                        break;
                    }
                }

                dropZone.event('send', (files) => {
                    if (files && files.length > 0) {
                        files[0].readData((content) => {
                            let extension;
                            let pos = files[0].name.lastIndexOf('.');

                            if (pos >= 0) {
                                extension = files[0].name.substring(pos + 1);
                            } else {
                                extension = files[0].mime.substring(files[0].mime.indexOf('/') + 1);
                            }

                            let url = `${Math.random().toString(36).substring(7) + new Date().getTime()}-${parseInt(this.state.selectedversion.substring(1), 10)}-${this.state.entity.versions[this.state.selectedversion].attributes[index].id}.${extension}`;
                            let entity = JSON.parse(JSON.stringify(this.state.entity));
                            entity.versions[this.state.selectedversion].attributes[index].value[entity.versions[this.state.selectedversion].attributes[index].value.length - 1] = { id: entity.versions[this.state.selectedversion].attributes[index].id, url: url, name: files[0].name, mime: files[0].mime, value: `data:${files[0].mime};base64,${this._base64ArrayBuffer(content)}` };
                            this['attribute' + entity.versions[this.state.selectedversion].attributes[index].id + '_' + (entity.versions[this.state.selectedversion].attributes[index].value.length - 1)].content = entity.versions[this.state.selectedversion].attributes[index].value[entity.versions[this.state.selectedversion].attributes[index].value.length - 1];
                            this.setState({ entity: entity });
                        });
                    }
                });
            }
        });
    }

    addRelation = (relation) => {
        let entity = this.state.entity;
        let item = { path: relation.path, namedpath: relation.namedpath };

        if (entity.relations) {
            entity.relations.push(item);
        } else {
            entity.relations = [item];
        }

        this.setState({ entity: entity });
    }

    /**
     * Toggles the log of the entity.
     */

    _toggleLog = () => {
        this.setState({ showlog: !this.state.showlog });
    }

    /**
     * Adds a new comment to the entity.
     */

    _addComment = () => {
        this.setState({ addcomment: true });
    }

    /**
     * Saves the comment to the entity.
     */

    _saveComment = () => {

        // Showing the progress indicator

        this.props.onLoading(true);

        Service.saveComment(this.state.entity.id, { 
            user:       this.props.context.user.name,
            date:       firebase.database.ServerValue.TIMESTAMP,
            comment:    this.comment.getValue()
        }).then((id) => {

            // Refreshing the comments

            let comment = this.state.comment;
            comment.push({ id: id, comment: this.comment.getValue(), date: new Date(), user: this.props.context.user.name});

            // Closing the comment box

            this.setState({ comment: comment, addcomment: false }, () => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing confirm message

                $.fireEvent('snackbar', { message: this.props.context.locale.dashboard.commentsaved });
            });
        }).catch((error) => {

            // Closing the comment box

            this.setState({ addcomment: false }, () => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        });
    }

    /**
     * Cancels the comment.
     */

    _cancelComment = () => {
        this.setState({ addcomment: false });
    }

    /**
     * Event that fires when the user wants to delete the entity.
     */

    _deleteEntity = () => {
        this.setState({ addcomment: false }, () => {
            this.props.onDelete(this.state.entity);
        });
    }

    /**
     * Event that fires when the screen mounted.
     */

    componentDidMount() {
        let users = JSON.parse(JSON.stringify(this.props.users));

        if (users[0].uid !== '-') {
            users.splice(0, 0, { uid: '-', name: '-' })
        }

        this.setState({ users: users }, () => {

            // Loads the entity

            if (this.props.context.environment.browser.info.mobile && this.props.id !== 'newentity') {
                this.loadEntity({ id: this.props.id }, this.props.readonly);
            }
        });
    }

    /** 
     * Entity view renderer.
     */

    render() {
        let getMultiValue = (value) => {
            if (value) {
                let text = '';

                for (let i = 0; i < value.length; i++) {
                    text += ((i === 0 ? '' : ', ') + value[i]);
                }

                return text;
            } else {
                return '';
            }
        };

        let formatDate = (date) => {
            return date ? new Datetime(new Date(date)).format('yyyy.mm.dd') : ''; 
        };

        let formatLogDate = (d) => {
            let date = new Date(d);
            return `${new Datetime(date).format('yyyy.mm.dd')} ${(date.getHours() < 10 ? '0' : '') + date.getHours()}:${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}:${(date.getSeconds() < 10 ? '0' : '') + date.getSeconds()}`; 
        };

        /**
         * Splits the specified text by using new lines.
         * 
         * @param   {String}    text        text that has to split
         * @return  {Object}    textElement generated text element
         */
        
        let breakLine = (text) => {
            let br = React.createElement('br');
            let regex = /\n/g;
            let lines = ('' + text).split(regex);

            return lines.map((line, index) => {
                if (index > 0) {
                    return <span key={"key_" + index}><br/>{line}</span>;
                } else {
                    return line;
                }
            });
        };

        /**
         * Attribute sorter.
         * 
         * @param   {Object}    a       attribute A
         * @param   {Object}    b       attribute B
         * @return  {Number}    result  comparison result
         */

        let sortByOrderNr = (a, b) => {
            if (a.ordernr < b.ordernr) {
                return -1;
            } else if (a.ordernr > b.ordernr) {
                return 1;
            } else {
                return 0;
            }                    
        };

        return (
            this.props.id && this.state.entity && <div className="entityview" style={{ height: (this.props.context.environment.screen.usefullheight - (this.props.context.environment.browser.info.mobile ? 32 : 116)) + 'px' }}>

                {/* Entity type and version */}

                { this.state.entity.entitytype && <div className="entityview-header horizontal center">
                    <div className="entityview-entitytype">{this.state.showlog ? this.props.context.locale.dashboard.logs.show.label : this.state.entity.entitytype.name}</div>
                    <div className="flex"></div>
                    <Label text={this.state.showlog ? this.props.context.locale.dashboard.logs.back.label : this.props.context.locale.dashboard.logs.show.label} tooltip={this.state.showlog ? this.props.context.locale.dashboard.logs.back.tooltip : this.props.context.locale.dashboard.logs.show.tooltip} link onTouch={this._toggleLog} />
                </div>}

                { this.state.edit && this.props.id === 'newentity' &&
                    <Combobox ref={c => {this.entitytype = c;}} tooltip={this.props.context.locale.dashboard.entitytype.tooltip} width={this.props.context.environment.browser.info.mobile ? 192 : 238} label={this.props.context.locale.dashboard.entitytype.label} labelwidth={160} labelposition="left" source={this.state.creationentitytypes} idattr="id" nameattr="name" onSelect={this._onEntityTypeSelected} />
                }

                {/* Log */}

                { this.state.showlog && <div className="entityview-scroller" style={{ display: this.state.showlog ? undefined : 'none', height: (this.props.context.environment.screen.usefullheight - (this.props.context.environment.browser.info.mobile ? 66 : 150)) + 'px' }}>
                    <Log context={this.props.context} entity={this.state.entity} />
                </div> }

                <div ref={c => {this.scrollcontainer = c;}} className="entityview-scroller" style={{ display: this.state.showlog ? 'none' : undefined, height: (this.props.context.environment.screen.usefullheight - (this.props.context.environment.browser.info.mobile ? 132 : 216)) + 'px' }}>

                    {/* Attributes */}

                    { ((this.state.entity.versions && this.state.entity.versions[this.state.selectedversion]) || this.props.id === 'newentity') && <div className="entityview-attributes vertical">

                        {/* Show */}

                        { !this.state.edit && this.state.entity.versions && this.state.entity.versions[this.state.selectedversion].attributes.sort(sortByOrderNr).map((item, index) =>
                            <div key={index}>
                                { !item.multivalue && <div className={`${item.type === 'picture' ? 'entityview-picture-container' : ''} horizontal`}>
                                    { item.primary ? <u><Label text={item.name + ':'} width={160} /></u> : <div><Label text={item.name + ':'} width={160} /></div> }
                                    { item.type === 'picture' && <div className="entityview-picture">
                                        <a href={`javascript:window.open("${item.value}")`}><img ref={c => {this['attribute' + item.id] = c;}} alt={`${item.name}`} src={item.value} width={120} height={120} /></a>
                                    </div> }
                                    { ['text', 'multilinetext', 'integer', 'float', 'radio'].indexOf(item.type) >= 0 && (item.primary ? <u><b><Label text={'' + item.value} /></b></u> : <b><Label text={'' + breakLine(item.value || '')} /></b>) }
                                    { item.type === 'date' && <b><Label text={formatDate(item.value)} /></b> }
                                    { item.type === 'externallink' && <b><Label link text={item.value} tooltip={item.value} onTouch={this._openExternalLink.bind(this, item.value)} /></b> }
                                    { item.type === 'checkbox' && <b><Label text={getMultiValue(item.value)} /></b> }
                                    { item.type === 'file' && item.value.name && <div className="horizontal center "><b><Label text={item.value.name} link onTouch={this._openExternalLink.bind(this, item.value.url)} /></b><img alt="Copy icon" src={CopyIcon} className="entityview-copy-icon" title={this.props.context.locale.dashboard.file.copyurl} onClick={this._copyURL.bind(this, item.value.url)} /></div> }
                                    { item.type === 'user' && item.user && <div className="horizontal"><b><Label text={item.user.name} /></b><a href={`mailto:${item.user.email}`} className="entityview-user-link">{item.user.email}</a></div> }
                                    { item.type === 'comment' && <div className="vertical">
                                        { this.state.comment.map((comment, commentindex) => 
                                            <div key={commentindex} className="entityview-comment vertical">
                                                <div className="entityview-comment-header horizontal">{comment.user} - {formatLogDate(comment.date)}</div>
                                                {breakLine(comment.comment || '')}
                                            </div>
                                        )}
                                        { this.state.addcomment && <Textarea ref={c => {this.comment = c;}} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 240) : 240} height={80} /> }
                                        { !this.state.addcomment && <img alt="Add comment icon" src={AddIcon} className="entityview-comment-icon" title={this.props.context.locale.dashboard.addcomment} onClick={this._addComment} /> }
                                        { this.state.addcomment && <div className="horizontal">
                                            <div className="flex"></div>
                                            <img alt="Save comment icon" src={SaveIcon} className="entityview-comment-icon" title={this.props.context.locale.dashboard.savecomment} onClick={this._saveComment} />
                                            <img alt="Cancel comment icon" src={CancelIcon} className="entityview-comment-icon" title={this.props.context.locale.dashboard.cancelcomment} onClick={this._cancelComment} />
                                        </div> }
                                    </div> }
                                </div> }
                                { item.multivalue && item.value.map((i, ind) => 
                                    <div key={ind} className={`${item.type === 'picture' ? 'entityview-picture-container' : ''} horizontal`}>
                                        <div><Label text={ind === 0 ? (item.name + ':') : ' '} width={160} /></div>
                                        { item.type === 'picture' && <div className="entityview-picture">
                                            <a href={`javascript:window.open("${item.value}")`}><img ref={c => {this['attribute' + item.id] = c;}} alt={`${item.name}`} src={item.value} width={120} height={120} /></a>
                                        </div> }
                                        { ['text', 'multilinetext', 'integer', 'float', 'radio'].indexOf(item.type) >= 0 && <b><Label text={'' + i} /></b> }
                                        { item.type === 'date' && <b><Label text={formatDate(i)} /></b> }
                                        { item.type === 'externallink' && <b><Label link text={'' + i} tooltip={'' + i} onTouch={this._openExternalLink.bind(this, i)} /></b> }
                                        { item.type === 'checkbox' && <b><Label text={getMultiValue(item.value)} /></b> }
                                        { item.type === 'file' && i.name && <div className="horizontal center "><b><Label text={i.name} link onTouch={this._openExternalLink.bind(this, i.url)} /></b><img alt="Copy icon" src={CopyIcon} className="entityview-copy-icon" title={this.props.context.locale.dashboard.file.copyurl} onClick={this._copyURL.bind(this, i.url)} /></div> }
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Modify */}

                        { this.state.edit && this.state.entity.versions && this.state.entity.versions[this.state.selectedversion].attributes.map((item, index) =>
                            <div key={index}>
                                { item.type === 'text' && !item.multivalue && <Textbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'text' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.text.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.text.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'multilinetext' && !item.multivalue && <Textarea ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} height={80} isrequired={item.mandatory} /> }
                                { item.type === 'multilinetext' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textarea ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} height={80} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.multilinetext.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.multilinetext.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'externallink' && !item.multivalue && <Textbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'externallink' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.externallink.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.externallink.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'integer' && !item.multivalue && <Numericbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} isnegative /> }
                                { item.type === 'integer' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Numericbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} isnegative />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.integer.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.integer.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'float' && !item.multivalue && <Numericbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} isfloat isnegative /> }
                                { item.type === 'float' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Numericbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} isfloat isnegative />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.float.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.date.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'date' && !item.multivalue && <Datebox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'date' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Datebox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.date.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.date.add} onClick={this._addMultivalueOnModify.bind(this, index)} />}
                                </div> )}
                                { item.type === 'picture' && <div className={`entityview-picture-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div id={`entityview-picture-${item.id}`} className="entityview-picture-edit"><img ref={c => {this['attribute' + item.id] = c;}} alt={`${item.name}`} src={(item.value && item.value.value) ? item.value.value : item.value} width={120} height={120} /></div>
                                </div> }
                                { item.type === 'radio' && this.props.context.environment.browser.info.mobile && <div className="entityview-radio-container vertical">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} bottomgap={8} /></div>
                                    { item.values.map((radioitem, radioindex) =>
                                        <Radio key={radioindex} ref={c => {this['attribute' + item.id + radioindex] = c}} label={radioitem} holder={this} group="aa" index={radioindex} />
                                    )}
                                </div> }
                                { item.type === 'radio' && !this.props.context.environment.browser.info.mobile && <div className="entityview-radio-container horizontal">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="vertical">
                                        { item.values.map((radioitem, radioindex) =>
                                            <Radio key={radioindex} ref={c => {this['attribute' + item.id + radioindex] = c}} topgap={radioindex === 0 ? 0 : 4} label={radioitem} holder={this} group={'attribute' + item.id} index={radioindex} />
                                        )}
                                    </div>
                                </div> }
                                { item.type === 'checkbox' && this.props.context.environment.browser.info.mobile && <div className="entityview-checkbox-container vertical">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} bottomgap={8} /></div>
                                    { item.values.map((checkboxitem, checkboxindex) =>
                                        <div key={checkboxindex} className="horizontal">
                                            <Checkbox ref={c => {this['attribute' + item.id + checkboxindex] = c}} topgap={checkboxindex === 0 ? 0 : 4} />
                                            <Label text={checkboxitem} leftgap={4} onTouch={this._toggleCheckbox.bind(this, 'attribute' + item.id + checkboxindex)} />
                                        </div>
                                    )}
                                </div> }
                                { item.type === 'checkbox' && !this.props.context.environment.browser.info.mobile && <div className="entityview-checkbox-container horizontal">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="vertical">
                                        { item.values.map((checkboxitem, checkboxindex) =>
                                            <div key={checkboxindex} className="horizontal">
                                                <Checkbox ref={c => {this['attribute' + item.id + checkboxindex] = c}} topgap={checkboxindex === 0 ? 0 : 4} />
                                                <Label text={checkboxitem} leftgap={4} onTouch={this._toggleCheckbox.bind(this, 'attribute' + item.id + checkboxindex)} />
                                            </div>
                                        )}
                                    </div>
                                </div> }
                                { item.type === 'file' && !item.multivalue && <div className={`entityview-file-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="entityview-file-edit horizontal">
                                        <Label ref={c => {this['attribute' + item.id + '_0'] = c;}} text={item.value ? item.value.name : ''} tooltip={item.value ? item.value.name : ''} />
                                        { <div id={`entityview-file-${item.id}`} className="entityview-file-add-icon horizontal vertical center center-justified" title={this.props.context.locale.dashboard.file.upload.tooltip} style={{ display: item.value ? 'none' : ''}}><div className="button-buttonlabel">{this.props.context.locale.dashboard.file.upload.label}</div></div> }
                                        { item.value && <img alt="Delete icon" src={DeleteIcon} className="entityview-file-delete-icon" title={this.props.context.locale.dashboard.file.upload.delete} onClick={this._deleteFile.bind(this, index)} /> }
                                    </div>
                                </div> }
                                { item.type === 'file' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical">
                                        <div className={`entityview-file-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                            <div><Label text={ind === 0 ? (item.name + ':') : ' '} width={160} isrequired={item.mandatory} /></div>
                                            <div className="entityview-file-edit horizontal">
                                                <Label ref={c => {this['attribute' + item.id + '_' + ind] = c;}} text={i ? (i.name || '') : ''} tooltip={i ? i.name : ''} />
                                                { <div id={`entityview-file-${item.id}_${ind}`} className="entityview-file-add-icon horizontal vertical center center-justified" title={this.props.context.locale.dashboard.file.upload.tooltip} style={{ display: (i.value || i.url) ? 'none' : ''}}><div className="button-buttonlabel">{this.props.context.locale.dashboard.file.upload.label}</div></div> }
                                                { (i.value || i.url) && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-deletefile-icon" title={this.props.context.locale.dashboard.file.upload.delete} onClick={this._deleteMultivalueOnModify.bind(this, index, ind)} /> }
                                            </div>
                                        </div> 
                                        { ind === (item.value.length - 1) && i.name && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.file.upload.add} onClick={this._addMultivalueOnModify.bind(this, index)} /> }
                                    </div>
                                )}
                                { item.type === 'user' && <Combobox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 238} isrequired={item.mandatory} source={this.state.users} idattr="uid" nameattr="name" /> }

                                {/* Placeholder */}

                                { item.placeholder && item.placeholder !== '' && item.type !== 'comment' && <div className="entityview-placeholder">{item.placeholder}</div> }
                            </div>
                        )}

                        {/* New */}

                        { this.state.edit && this.props.id === 'newentity' && this.state.entitytype.attributes.map((item, index) =>
                            <div key={index}>
                                { item.type === 'text' && !item.multivalue && <Textbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'text' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.text.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.text.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'multilinetext' && !item.multivalue && <Textarea ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} height={80} isrequired={item.mandatory} /> }
                                { item.type === 'multilinetext' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textarea ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} height={80} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.multilinetext.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.multilinetext.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'externallink' && !item.multivalue && <Textbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'externallink' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Textbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.externallink.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.externallink.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'integer' && !item.multivalue && <Numericbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} isnegative /> }
                                { item.type === 'integer' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Numericbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} isnegative />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.integer.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.integer.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'float' && !item.multivalue && <Numericbox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} isfloat isnegative /> }
                                { item.type === 'float' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Numericbox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} isfloat isnegative />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.float.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.float.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'date' && !item.multivalue && <Datebox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 240} isrequired={item.mandatory} /> }
                                { item.type === 'date' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical"><div className="horizontal">
                                        <Datebox ref={c => {this['attribute' + item.id + '_' + ind] = c;}} label={ind === 0 ? (item.name + ':') : ' '} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 94) : 210} isrequired={item.mandatory} />
                                        { ind > 0 && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-delete-icon" title={this.props.context.locale.dashboard.date.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                        </div>{ ind === (item.value.length - 1) && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.date.add} onClick={this._addMultivalueOnNew.bind(this, index)} />}
                                </div> )}
                                { item.type === 'picture' && <div className={`entityview-picture-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div id={`entityview-picture-${item.id}`} className="entityview-picture-edit"><img ref={c => {this['attribute' + item.id] = c;}} alt={`${item.name}`} src={item.value} width={120} height={120} /></div>
                                </div> }
                                { item.type === 'radio' && this.props.context.environment.browser.info.mobile && <div className="entityview-radio-container vertical">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} bottomgap={8} /></div>
                                    { item.values.map((radioitem, radioindex) =>
                                        <Radio key={radioindex} ref={c => {this['attribute' + item.id + radioindex] = c}} topgap={radioindex === 0 ? 0 : 4} label={radioitem} holder={this} group={'attribute' + item.id} index={radioindex} />
                                    )}
                                </div> }
                                { item.type === 'radio' && !this.props.context.environment.browser.info.mobile && <div className="entityview-radio-container horizontal">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="vertical">
                                        { item.values.map((radioitem, radioindex) =>
                                            <Radio key={radioindex} ref={c => {this['attribute' + item.id + radioindex] = c}} topgap={radioindex === 0 ? 0 : 4} label={radioitem} holder={this} group={'attribute' + item.id} index={radioindex} />
                                        )}
                                    </div>
                                </div> }
                                { item.type === 'checkbox' && this.props.context.environment.browser.info.mobile && <div className="entityview-checkbox-container vertical">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} bottomgap={8} /></div>
                                    { item.values.map((checkboxitem, checkboxindex) =>
                                        <div key={checkboxindex} className="horizontal">
                                            <Checkbox ref={c => {this['attribute' + item.id + checkboxindex] = c}} topgap={checkboxindex === 0 ? 0 : 4} />
                                            <Label text={checkboxitem} leftgap={4} onTouch={this._toggleCheckbox.bind(this, 'attribute' + item.id + checkboxindex)} />
                                        </div>
                                    )}
                                </div> }
                                { item.type === 'checkbox' && !this.props.context.environment.browser.info.mobile && <div className="entityview-checkbox-container horizontal">
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="vertical">
                                        { item.values.map((checkboxitem, checkboxindex) =>
                                            <div key={checkboxindex} className="horizontal">
                                                <Checkbox ref={c => {this['attribute' + item.id + checkboxindex] = c}} topgap={checkboxindex === 0 ? 0 : 4} />
                                                <Label text={checkboxitem} leftgap={4} onTouch={this._toggleCheckbox.bind(this, 'attribute' + item.id + checkboxindex)} />
                                            </div>
                                        )}
                                    </div>
                                </div> }
                                { item.type === 'file' && !item.multivalue && <div className={`entityview-file-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                    <div><Label text={item.name + ':'} width={160} isrequired={item.mandatory} /></div>
                                    <div className="entityview-file-edit horizontal">
                                        <Label ref={c => {this['attribute' + item.id + '_0'] = c;}} text={item.value ? item.value[0].name : ''} tooltip={item.value ? item.value[0].name : ''} />
                                        { <div id={`entityview-file-${item.id}`} className="entityview-file-add-icon horizontal vertical center center-justified" title={this.props.context.locale.dashboard.file.upload.tooltip} style={{ display: item.value ? 'none' : ''}}><div className="button-buttonlabel">{this.props.context.locale.dashboard.file.upload.label}</div></div> }
                                        { item.value && <img alt="Delete icon" src={DeleteIcon} className="entityview-file-delete-icon" title={this.props.context.locale.dashboard.file.delete} onClick={this._deleteFile.bind(this, index)} /> }
                                    </div>
                                </div> }
                                { item.type === 'file' && item.multivalue && item.value && item.value.map((i, ind) =>
                                    <div key={ind} className="vertical">
                                        <div className={`entityview-file-container ${this.props.context.environment.browser.info.mobile ? 'vertical' : 'horizontal'}`}>
                                            <div><Label text={ind === 0 ? (item.name + ':') : ' '} width={160} isrequired={item.mandatory} /></div>
                                            <div className="entityview-file-edit horizontal">
                                                <Label ref={c => {this['attribute' + item.id + '_' + ind] = c;}} text={i ? (i.name || '') : ''} tooltip={i ? i.name : ''} />
                                                { <div id={`entityview-file-${item.id}_${ind}`} className="entityview-file-add-icon horizontal vertical center center-justified" title={this.props.context.locale.dashboard.file.upload.tooltip} style={{ display: i.value ? 'none' : ''}}><div className="button-buttonlabel">{this.props.context.locale.dashboard.file.upload.label}</div></div> }
                                                { i.value && <img alt="Delete icon" src={DeleteIcon} className="entityview-multivalue-deletefile-icon" title={this.props.context.locale.dashboard.file.delete} onClick={this._deleteMultivalueOnNew.bind(this, index, ind)} /> }
                                            </div>
                                        </div> 
                                        { ind === (item.value.length - 1) && i.name && <img alt="Add icon" src={AddIcon} className="entityview-multivalue-add-icon" style={{ marginLeft: (this.props.context.environment.browser.info.mobile ? 0 : 160) + 'px' }} title={this.props.context.locale.dashboard.file.add} onClick={this._addMultivalueOnNew.bind(this, index)} /> }
                                    </div>
                                )}
                                { item.type === 'user' && <Combobox ref={c => {this['attribute' + item.id] = c;}} label={item.name + ':'} labelwidth={160} labelposition={this.props.context.environment.browser.info.mobile ? 'top' : 'left'} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 238} isrequired={item.mandatory} source={this.state.users} idattr="uid" nameattr="name" /> }

                                {/* Placeholder */}

                                { item.placeholder && item.placeholder !== '' && item.type !== 'comment' && <div className="entityview-placeholder">{item.placeholder}</div> }
                            </div>
                        )}

                        {/* Log */}

                        { this.state.entity.versions && this.state.entity.versions[this.state.selectedversion] && !this.state.edit && <div className="entityview-log">
                            <div className="horizontal">
                                <Label text={this.props.context.locale.dashboard.log.modificationdate} width={160} />
                                <b><Label text={formatLogDate(this.state.entity.versions[this.state.selectedversion].modificationdate)} /></b>
                            </div>
                            <div className="horizontal">
                                <Label text={this.props.context.locale.dashboard.log.modifiedby} width={160} />
                                <b><Label text={this.state.entity.versions[this.state.selectedversion].modifiedby.name} /></b>
                            </div>
                            <div className="entityview-log-email horizontal">
                                <Label text="" width={160} />
                                <a href={`mailto:${this.state.entity.versions[this.state.selectedversion].modifiedby.email}`} className="entityview-link">{this.state.entity.versions[this.state.selectedversion].modifiedby.email}</a>
                            </div>
                        </div>}
                    </div>}

                    {/* Tags */}

                    { !this.state.edit && this.state.entity.tags && <div className="entityview-tags vertical">
                        <div className="entityview-tag-title">{this.props.context.locale.dashboard.tags}</div>
                        { !this.state.entity.tags && <div className="entityview-tag-empty">-</div> }
                        <div className="wrap">
                            { this.state.entity.tags.map((item, index) =>
                                <div key={index} className="entityview-tag">{item}</div>
                            )}
                        </div>
                    </div>}

                    { this.state.edit && <div className="vertical">
                        <div className="entityview-tag-title">{this.props.context.locale.dashboard.tags}</div>
                        <Textarea ref={c => {this.tags = c;}} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 60) : 400} height={80} />
                    </div>}

                    {/* Relations */}

                    { !this.state.edit && this.state.entity.relations && <div className="vertical">
                        <div className="entityview-relation-title">{this.props.context.locale.dashboard.relation.title}</div>
                        { !this.state.entity.relations && <div className="entityview-relation-empty">-</div> }
                        { this.state.entity.relations && this.state.entity.relations.map((item, index) =>
                            <Label key={index} link text={item.namedpath} tooltip={item.namedpath} onTouch={this.props.onGotoRelation.bind(this, item)} />
                        )}
                    </div>}

                    { this.state.edit && <div className="vertical">
                        <div className="entityview-relation-title">{this.props.context.locale.dashboard.relation.title}</div>
                        { this.state.entity.relations && this.state.entity.relations.map((item, index) =>
                            <div key={index} className="horizontal">
                                <Label link text={item.namedpath} tooltip={item.namedpath} />
                                <img alt="Delete icon" src={DeleteIcon} className="entityview-relation-delete-icon" title={this.props.context.locale.dashboard.relation.delete} onClick={this._deleteRelation.bind(this, index)} />
                            </div>
                        )}
                        <img alt="Add icon" src={AddIcon} className="entityview-relation-add-icon" title={this.props.context.locale.dashboard.relation.add} onClick={this.props.onNewRelation} />
                    </div>}
                </div>

                {/* Buttons */}

                <div className="entityview-buttons horizontal" style={{ display: this.state.showlog ? 'none' : undefined }}>
                    { this.state.edit && <Button label={this.props.context.locale.dashboard.buttons.cancel.label} tooltip={this.props.context.locale.dashboard.buttons.cancel.tooltip} width={60} onTouch={this._cancel} /> }
                    { !this.state.edit && this.props.id && this.state.entity.entitytype && this.state.entity.writable && <Button label={this.props.context.locale.dashboard.buttons.delete.label} tooltip={`${this.state.entity.entitytype.name} ${this.props.context.locale.dashboard.buttons.delete.tooltip}`} width={60} onTouch={this._deleteEntity} /> }
                    { !this.state.edit && this.props.id && this.state.entity.entitytype && this.state.entity.writable && <Button label={this.props.context.locale.dashboard.buttons.modify.label} tooltip={`${this.state.entity.entitytype.name} ${this.props.context.locale.dashboard.buttons.modify.tooltip}`} width={60} leftgap={16} onTouch={this._modify} /> }
                    <div className="flex"></div>
                    { this.state.edit && <Button label={this.props.context.locale.dashboard.buttons.save.label} tooltip={this.props.context.locale.dashboard.buttons.save.tooltip} width={60} onTouch={this._save} /> }
                    { !this.state.edit && this.props.id && <Button label={this.props.context.locale.dashboard.buttons.close.label} tooltip={this.state.entity.entitytype ? `${this.state.entity.entitytype.name} ${this.props.context.locale.dashboard.buttons.close.tooltip}` : this.props.context.locale.dashboard.buttons.close.label} width={60} onTouch={this.props.onClose.bind(this, this.props.id)} /> }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Entityview/Entityview.js